import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import type { UserRole } from '../types/user';
import { canAccessRoute } from '../lib/permissions';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole; // Rôle minimum requis pour accéder à cette route
}

function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const { role } = usePermissions();
  const location = useLocation();

  // Si on est en train de charger l'état d'auth, afficher un loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Si pas d'utilisateur connecté, rediriger vers login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Vérifier si l'utilisateur a le rôle requis pour cette route
  if (requiredRole) {
    const roleHierarchy: Record<UserRole, number> = {
      user: 1,
      admin: 2,
      superadmin: 3,
    };

    const userRoleLevel = roleHierarchy[role];
    const requiredRoleLevel = roleHierarchy[requiredRole];

    if (userRoleLevel < requiredRoleLevel) {
      // L'utilisateur n'a pas le rôle requis, rediriger vers la page d'accueil
      return <Navigate to="/" replace />;
    }
  }

  // Vérifier l'accès à la route selon les permissions
  if (!canAccessRoute(location.pathname, role)) {
    return <Navigate to="/" replace />;
  }

  // Si utilisateur connecté et autorisé, afficher le contenu
  return <>{children}</>;
}

export default ProtectedRoute;
